import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { PageLoader } from '@ui/PageLoader';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ERROR_CAUSE_REASON } from '@shared/api/constants';
import { routesMap } from '@shared/lib';

import {
  AUTH_MFA_TYPE,
  AUTH_OTP_LENGTH,
  AUTH_QUERY_KEY,
} from '../../constants';
import {
  sendSignInOtp,
  signInWithPassword,
  verifySignInOtp,
} from '../../services';
import { useAuthContext } from '../../context';
import { getSafeRedirect } from '../../lib';

import { Login } from '../../ui/auth/Login';
import { Otp } from '../../ui/auth/Otp';

import styles from './auth-page.module.css';

type Step = 'credentials' | 'otp';
const OTP_RESEND_DELAY_SECONDS = 60;
const OTP_RESEND_RATE_LIMITED_ERROR =
  'You need to wait before requesting another OTP';

const getRedirect = (search: string) => {
  const params = new URLSearchParams(search);

  return getSafeRedirect(params.get('redirect'));
};

export const AuthPage = () => {
  const { isAuthorized, isLoading } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaType, setMfaType] = useState(AUTH_MFA_TYPE);
  const [error, setError] = useState('');
  const [resendDelay, setResendDelay] = useState(OTP_RESEND_DELAY_SECONDS);

  const redirect = getRedirect(location.search);

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: [AUTH_QUERY_KEY] });
    navigate(redirect, { replace: true });
  };

  const { mutateAsync: signIn, isPending: isSigningIn } = useMutation({
    mutationFn: signInWithPassword,
  });
  const { mutateAsync: sendOtp, isPending: isSendingOtp } = useMutation({
    mutationFn: sendSignInOtp,
  });
  const { mutateAsync: verifyOtp, isPending: isVerifyingOtp } = useMutation({
    mutationFn: verifySignInOtp,
  });

  useEffect(() => {
    if (step !== 'otp' || resendDelay === 0) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setResendDelay((value) => Math.max(0, value - 1));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [resendDelay, step]);

  const requestEmailCode = async (
    nextMfaToken: string,
    nextMfaType: string,
  ) => {
    if (nextMfaType !== AUTH_MFA_TYPE) {
      return {
        success: false,
        error:
          'This verification method is not supported. Please contact support.',
      };
    }

    return sendOtp({
      mfaToken: nextMfaToken,
      mfaType: nextMfaType,
    });
  };

  const handleCredentialsSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Enter your email and password');
      return;
    }

    const response = await signIn({ email, password });

    if (!response.success || !response.data) {
      setError(response.error || 'Failed to sign in');
      return;
    }

    if (response.data.completed) {
      await handleSuccess();
      return;
    }

    if (!response.data.mfaToken) {
      setError('Failed to start code verification');
      return;
    }

    const nextMfaToken = response.data.mfaToken;
    const nextMfaType = response.data.mfaType;
    const sendResponse = await requestEmailCode(nextMfaToken, nextMfaType);

    const isResendRateLimited = sendResponse.httpStatus === 400;

    if (!sendResponse.success) {
      if (isResendRateLimited) {
        setError(OTP_RESEND_RATE_LIMITED_ERROR);
        return;
      }

      setError(sendResponse.error || 'Failed to send verification code');
      return;
    }

    setMfaToken(nextMfaToken);
    setMfaType(nextMfaType);
    setCode('');
    setResendDelay(OTP_RESEND_DELAY_SECONDS);
    setStep('otp');
  };

  const submitOtpCode = async (passcode: string) => {
    setError('');

    if (passcode.length !== AUTH_OTP_LENGTH) {
      setError(`Enter the ${AUTH_OTP_LENGTH}-digit code`);
      return;
    }

    const response = await verifyOtp({
      mfaToken,
      mfaType,
      passcode,
    });

    if (!response.success) {
      if (
        response.httpStatus === 400 ||
        response.causeReason === API_ERROR_CAUSE_REASON.mfaPasscodeInvalid
      ) {
        setCode('');
        setError(response.error || 'Wrong verification code');
        return;
      }

      setCode('');
      setMfaToken('');
      setStep('credentials');
      setError(
        response.error ||
          'Code is invalid or expired. Sign in again to request a new code.',
      );
      return;
    }

    await handleSuccess();
  };

  const handleOtpSubmit = async (event: FormEvent) => {
    event.preventDefault();

    await submitOtpCode(code);
  };

  const handleResend = async () => {
    setError('');

    if (!mfaToken) {
      setStep('credentials');
      setError('Failed to request a new code. Sign in again.');
      return;
    }

    const sendResponse = await requestEmailCode(mfaToken, mfaType);

    if (!sendResponse.success) {
      const isResendRateLimited = sendResponse.httpStatus === 400;

      if (isResendRateLimited) {
        setCode('');
        setResendDelay(OTP_RESEND_DELAY_SECONDS);
        setError(OTP_RESEND_RATE_LIMITED_ERROR);
        return;
      }

      setError(sendResponse.error || 'Failed to send verification code');
      return;
    }

    setCode('');
    setResendDelay(OTP_RESEND_DELAY_SECONDS);
  };

  const handleOtpChange = (value: string) => {
    const nextCode = value.replace(/\D/g, '').slice(0, AUTH_OTP_LENGTH);
    setCode(nextCode);

    if (nextCode.length === AUTH_OTP_LENGTH && !isVerifyingOtp) {
      submitOtpCode(nextCode);
    }
  };

  if (isLoading) {
    return <PageLoader className={styles.loader} />;
  }

  if (isAuthorized) {
    return <Navigate to={redirect} replace />;
  }

  return (
    <main className={styles.page}>
      <header className={styles.topbar}>
        <Link to={routesMap.auth} aria-label="Go to sign in">
          <img className={styles.logo} src="/gcard-logotype.svg" alt="GCARD" />
        </Link>
      </header>
      <div className={styles.center}>
        {step === 'credentials' ? (
          <Login
            email={email}
            password={password}
            error={error}
            isPasswordVisible={isPasswordVisible}
            isSubmitting={isSigningIn || isSendingOtp}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onPasswordVisibilityToggle={() =>
              setIsPasswordVisible((value) => !value)
            }
            onSubmit={handleCredentialsSubmit}
          />
        ) : (
          <Otp
            code={code}
            codeLength={AUTH_OTP_LENGTH}
            error={error}
            isResendDisabled={resendDelay > 0 || isSendingOtp}
            resendDelay={resendDelay}
            onCodeChange={handleOtpChange}
            onResend={handleResend}
            onSubmit={handleOtpSubmit}
          />
        )}
      </div>
    </main>
  );
};
